import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  getMapSnapshots,
  getSnapshotSignedUrl,
  setRecordSnapshotSelection,
  MulchMapSnapshotSummary,
} from "api/MulchMap";
import { themevals } from "theme/themevals";

/**
 * SnapshotIncludePicker — Session 28 hotfix 6 (Phase 2)
 *
 * The chip + popover that replaces the "Include site map" checkbox in
 * the record header (Quote first; Invoice + PO in Phase 3). Shows a
 * count badge on the chip, opens a popover on click, lets the rep pick
 * which snapshots to include and in what order. Saves on popover close.
 *
 * Props:
 *   recordType: which record table to save against
 *   recordId:   which record's mulchMapSnapshotIds column to update
 *   mulchMapId: the attached map version — pull snapshots for this
 *   initialSelectedIds: what was persisted last time; the picker opens
 *                       with these pre-selected in the order given
 *
 * Doesn't render anything when the attached map has zero snapshots, to
 * match today's behavior (the checkbox is only shown when snapshotCount
 * > 0). Loads snapshots on mount so the chip badge count is accurate
 * before the popover ever opens.
 */

interface Props {
  recordType: "quote" | "invoice" | "purchase_order";
  recordId: string;
  mulchMapId: string | null;
  initialSelectedIds: string[];
}

interface SnapshotWithUrl {
  snapshot: MulchMapSnapshotSummary;
  signedUrl: string | null;
}

const BASEMAP_LABELS: Record<string, string> = {
  google_hybrid: "Google 2D",
  mapbox_sat: "Mapbox",
  esri_sat: "Esri",
  google_3d: "Google 3D",
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    userSelect: "none",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
  },
  chipInactive: {
    background: "transparent",
    color: themevals.colors.dkgrey,
    border: "1px solid " + themevals.colors.inputOutline,
  },
  chipActive: {
    background: "#E6F1FB",
    color: "#0C447C",
    border: "1px solid #85B7EB",
  },
  countBadge: {
    background: "#185FA5",
    color: "#fff",
    padding: "1px 7px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  popover: {
    position: "absolute",
    top: "calc(100% + 10px)",
    left: 0,
    zIndex: 100,
    background: "#fff",
    border: "1px solid #E4E7EB",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    minWidth: 560,
    maxWidth: 720,
  },
  popoverTail: {
    position: "absolute",
    top: -7,
    left: 20,
    width: 12,
    height: 12,
    background: "#fff",
    borderLeft: "1px solid #E4E7EB",
    borderTop: "1px solid #E4E7EB",
    transform: "rotate(45deg)",
  },
  popoverHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 12,
  },
  popoverTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 500,
    color: "#111",
    fontFamily: "Poppins, sans-serif",
  },
  popoverHint: {
    margin: 0,
    fontSize: 11,
    color: "#8A8B8F",
    fontFamily: "Poppins, sans-serif",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: 12,
  },
  card: {
    borderRadius: 8,
    overflow: "hidden",
    cursor: "pointer",
    transition: "border-color 0.15s ease, opacity 0.15s ease",
    background: "#F9FAFB",
  },
  cardSelected: {
    border: "2px solid #185FA5",
    opacity: 1,
  },
  cardUnselected: {
    border: "0.5px solid #E4E7EB",
    opacity: 0.85,
  },
  thumb: {
    aspectRatio: "4 / 3",
    background: "#E4E7EB",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    color: "#78716C",
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  basemapPill: {
    position: "absolute",
    top: 6,
    left: 6,
    padding: "2px 6px",
    background: "rgba(17,17,17,0.75)",
    color: "#fff",
    borderRadius: 3,
    fontSize: 9,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    pointerEvents: "none",
  },
  orderBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    background: "#185FA5",
    color: "#fff",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 500,
    pointerEvents: "none",
  },
  cardFoot: {
    padding: "5px 8px",
    fontSize: 10,
    color: "#5D5E62",
    fontFamily: "Poppins, sans-serif",
  },
  footer: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "0.5px solid #E4E7EB",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerNote: {
    margin: 0,
    fontSize: 11,
    color: "#5D5E62",
    fontFamily: "Poppins, sans-serif",
  },
  saveError: {
    marginTop: 8,
    padding: "6px 10px",
    background: "#FEE2E2",
    border: "1px solid #FCA5A5",
    borderRadius: 4,
    fontSize: 11,
    color: "#991B1B",
  },
};

// Sortable card — wraps one snapshot thumbnail. Only rendered when the
// snapshot is currently selected (unselected cards don't need drag).
const SortableCard: React.FC<{
  id: string;
  order: number;
  data: SnapshotWithUrl;
  onToggle: (id: string) => void;
}> = ({ id, order, data, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const s = data.snapshot;
  const basemapLabel = s.basemap
    ? BASEMAP_LABELS[s.basemap] || s.basemap
    : null;

  const style: React.CSSProperties = {
    ...styles.card,
    ...styles.cardSelected,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: isDragging ? "grabbing" : "pointer",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={(e) => {
        // Distinguish click from drag: dnd-kit sets isDragging during
        // drag, but the click still fires on release. Guard via a data
        // attr to skip the toggle if a drag just ended.
        if (isDragging) return;
        onToggle(id);
        e.stopPropagation();
      }}
      {...attributes}
      {...listeners}
    >
      <div style={styles.thumb}>
        {data.signedUrl ? (
          <img
            src={data.signedUrl}
            alt={"Snapshot " + order}
            style={styles.thumbImg}
          />
        ) : (
          <span>Loading{"\u2026"}</span>
        )}
        {basemapLabel && <span style={styles.basemapPill}>{basemapLabel}</span>}
        <span style={styles.orderBadge}>{order}</span>
      </div>
      <div style={styles.cardFoot}>
        {moment.utc(s.takenAt).local().format("MMM D \u00b7 h:mm a")}
      </div>
    </div>
  );
};

// Static card — for unselected snapshots. Click adds to selection.
const StaticCard: React.FC<{
  id: string;
  data: SnapshotWithUrl;
  onToggle: (id: string) => void;
}> = ({ id, data, onToggle }) => {
  const s = data.snapshot;
  const basemapLabel = s.basemap
    ? BASEMAP_LABELS[s.basemap] || s.basemap
    : null;

  return (
    <div
      style={{ ...styles.card, ...styles.cardUnselected }}
      onClick={(e) => {
        onToggle(id);
        e.stopPropagation();
      }}
    >
      <div style={styles.thumb}>
        {data.signedUrl ? (
          <img
            src={data.signedUrl}
            alt="Snapshot"
            style={styles.thumbImg}
          />
        ) : (
          <span>Loading{"\u2026"}</span>
        )}
        {basemapLabel && <span style={styles.basemapPill}>{basemapLabel}</span>}
      </div>
      <div style={styles.cardFoot}>
        {moment.utc(s.takenAt).local().format("MMM D \u00b7 h:mm a")}
      </div>
    </div>
  );
};

const SnapshotIncludePicker: React.FC<Props> = ({
  recordType,
  recordId,
  mulchMapId,
  initialSelectedIds,
}) => {
  const [snapshots, setSnapshots] = useState<SnapshotWithUrl[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const [open, setOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Load snapshots for the attached map version. Fetches signed URLs in
  // parallel so thumbs render as fast as possible. If mulchMapId is null,
  // we render nothing (there can't be any snapshots).
  useEffect(() => {
    if (!mulchMapId) {
      setSnapshots([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const snaps = await getMapSnapshots(mulchMapId);
      if (cancelled) return;
      const initial: SnapshotWithUrl[] = snaps.map((s) => ({
        snapshot: s,
        signedUrl: null,
      }));
      setSnapshots(initial);
      await Promise.all(
        snaps.map(async (s, i) => {
          const url = await getSnapshotSignedUrl(s.id);
          if (cancelled) return;
          setSnapshots((prev) => {
            const next = [...prev];
            if (next[i] && next[i].snapshot.id === s.id) {
              next[i] = { ...next[i], signedUrl: url };
            }
            return next;
          });
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [mulchMapId]);

  // Prune selectedIds that no longer resolve against the loaded snapshot
  // list (deleted since last save). Silent; the persisted list gets
  // re-saved on next popover close.
  useEffect(() => {
    if (snapshots.length === 0) return;
    const known = new Set(snapshots.map((s) => s.snapshot.id));
    setSelectedIds((prev) => prev.filter((id) => known.has(id)));
  }, [snapshots]);

  // Save the current selection to the backend. Debounced by wrapping in
  // popover-close: we don't save on every toggle, only when the popover
  // closes.
  const saveSelection = useCallback(
    async (ids: string[]) => {
      if (!recordId) return;
      setSaveError(null);
      const res = await setRecordSnapshotSelection(
        recordType,
        recordId,
        ids,
      );
      if ("error" in res) {
        setSaveError(res.error);
      }
    },
    [recordType, recordId],
  );

  // Click-outside-to-close handler. Only active while open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (!wrapper.contains(e.target as Node)) {
        setOpen(false);
        saveSelection(selectedIds);
      }
    };
    // Delay to avoid firing on the same click that opened the popover.
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handler);
    };
  }, [open, selectedIds, saveSelection]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSelectedIds((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const snapshotById = useMemo(() => {
    const m = new Map<string, SnapshotWithUrl>();
    for (const s of snapshots) m.set(s.snapshot.id, s);
    return m;
  }, [snapshots]);

  const selectedSnapshots = useMemo(
    () =>
      selectedIds
        .map((id) => snapshotById.get(id))
        .filter((s): s is SnapshotWithUrl => !!s),
    [selectedIds, snapshotById],
  );

  const unselectedSnapshots = useMemo(
    () =>
      snapshots.filter((s) => !selectedIds.includes(s.snapshot.id)),
    [snapshots, selectedIds],
  );

  // Nothing to show if the attached map has no snapshots. Matches the
  // pre-Phase-2 behavior where the checkbox was hidden.
  if (!mulchMapId || snapshots.length === 0) {
    return null;
  }

  const totalCount = snapshots.length;
  const selectedCount = selectedIds.length;
  const active = selectedCount > 0;

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <div
        style={{
          ...styles.chip,
          ...(active ? styles.chipActive : styles.chipInactive),
        }}
        onClick={() => {
          if (open) {
            setOpen(false);
            saveSelection(selectedIds);
          } else {
            setOpen(true);
          }
        }}
        title={
          totalCount === 1
            ? "Include the site map in the PDF"
            : selectedCount + " of " + totalCount + " site maps selected for the PDF"
        }
      >
        <span>Include site map</span>
        {totalCount > 1 && (
          <span style={styles.countBadge}>
            {selectedCount} / {totalCount}
          </span>
        )}
        {totalCount === 1 && selectedCount === 1 && (
          <span style={{ ...styles.countBadge, padding: "1px 5px" }}>1</span>
        )}
      </div>

      {open && (
        <div style={styles.popover} onMouseDown={(e) => e.stopPropagation()}>
          <div style={styles.popoverTail} />
          <div style={styles.popoverHeader}>
            <p style={styles.popoverTitle}>Snapshots on this map</p>
            <p style={styles.popoverHint}>
              Click to include{selectedCount > 1 ? " \u00b7 drag to reorder" : ""}
            </p>
          </div>

          <div style={styles.grid}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedIds}
                strategy={horizontalListSortingStrategy}
              >
                {selectedSnapshots.map((s, idx) => (
                  <SortableCard
                    key={s.snapshot.id}
                    id={s.snapshot.id}
                    order={idx + 1}
                    data={s}
                    onToggle={toggle}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {unselectedSnapshots.map((s) => (
              <StaticCard
                key={s.snapshot.id}
                id={s.snapshot.id}
                data={s}
                onToggle={toggle}
              />
            ))}
          </div>

          <div style={styles.footer}>
            <p style={styles.footerNote}>
              {selectedCount === 0
                ? "No site maps will be included in the PDF."
                : "Selected snapshots appear as pages at the end of the PDF, in order."}
            </p>
          </div>

          {saveError && <div style={styles.saveError}>{saveError}</div>}
        </div>
      )}
    </div>
  );
};

export default SnapshotIncludePicker;
